import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { getEvents } from '../api';
import { resolveEventImage } from '../utils/eventImage';

const Events = () => {
   const [events, setEvents] = useState([]);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState(null);

   useEffect(() => {
      loadEvents();
   }, []);

   const loadEvents = async () => {
      try {
         setError(null);
         const data = await getEvents();
         setEvents(data);
      } catch (err) {
         console.error('Failed to load events:', err);
         setError('Failed to load events. Please try again later.');
      }
      setLoading(false);
   };

   // Group events by year
   const eventsByYear = events.reduce((acc, event) => {
      const year = event.year;
      if (!acc[year]) acc[year] = [];
      acc[year].push(event);
      return acc;
   }, {});

   const years = Object.keys(eventsByYear).sort((a, b) => b - a);

   return (
      <div>
         {/* Hero Section */}
         <div style={styles.hero} className="subpage-hero">
            <motion.h1
               initial={{ opacity: 0, y: 30 }}
               animate={{ opacity: 1, y: 0 }}
               transition={{ duration: 0.8, delay: 0.3 }}
               style={styles.heroTitle}
            >
               Events
            </motion.h1>
         </div>

         {loading ? (
            <div style={styles.loading}>Loading...</div>
         ) : error ? (
            <div style={styles.error}>{error}</div>
         ) : (
            years.map((year, yearIndex) => (
               <section
                  key={year}
                  style={yearIndex % 2 === 0 ? styles.section : styles.sectionDark}
               >
                  <div style={styles.container}>
                     <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        style={styles.yearHeader}
                     >
                        <h2 style={yearIndex % 2 === 0 ? styles.yearTitle : styles.yearTitleLight}>
                           {year}
                        </h2>
                        <div style={styles.divider} />
                     </motion.div>

                     <div style={styles.eventsGrid}>
                        {eventsByYear[year].map((event, index) => (
                           <motion.div
                              key={event.id}
                              initial={{ opacity: 0, y: 40 }}
                              whileInView={{ opacity: 1, y: 0 }}
                              viewport={{ once: true }}
                              transition={{ duration: 0.6, delay: index * 0.1 }}
                              style={yearIndex % 2 === 0 ? styles.eventCard : styles.eventCardDark}
                           >
                              <div style={styles.imageWrapper}>
                                 {resolveEventImage(event.image) ? (
                                    <img
                                       src={resolveEventImage(event.image)}
                                       alt={`${event.title} 포스터`}
                                       style={styles.eventImage}
                                    />
                                 ) : (
                                    <div style={styles.imagePlaceholder}>
                                       <span style={styles.placeholderTitle}>{event.title}</span>
                                    </div>
                                 )}
                              </div>
                              <div style={styles.eventContent}>
                                 <p style={styles.eventDate}>{event.date}</p>
                                 <h3 style={yearIndex % 2 === 0 ? styles.eventTitle : styles.eventTitleLight}>
                                    {event.title}
                                 </h3>
                                 <p style={yearIndex % 2 === 0 ? styles.eventVenue : styles.eventVenueLight}>
                                    {event.venue}
                                 </p>
                                 {event.time && (
                                    <p style={yearIndex % 2 === 0 ? styles.eventTime : styles.eventTimeLight}>
                                       {event.time}
                                    </p>
                                 )}
                                 {event.subtitle && (
                                    <p style={styles.eventSubtitle}>{event.subtitle}</p>
                                 )}
                              </div>
                           </motion.div>
                        ))}
                     </div>
                  </div>
               </section>
            ))
         )}
      </div>
   );
};

const styles = {
   hero: {
      position: 'relative',
      height: '50vh',
      minHeight: '400px',
      background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
   },
   heroTitle: {
      fontFamily: "'Cormorant Garamond', serif",
      fontSize: 'clamp(2.5rem, 8vw, 5rem)',
      fontWeight: 300,
      color: 'white',
      letterSpacing: '0.3em',
      textTransform: 'uppercase',
      margin: 0,
   },
   loading: {
      textAlign: 'center',
      padding: '100px',
      fontFamily: "'Montserrat', sans-serif",
      color: '#888',
      backgroundColor: '#fafafa',
   },
   error: {
      textAlign: 'center',
      padding: '100px',
      fontFamily: "'Montserrat', sans-serif",
      color: '#c9a227',
      backgroundColor: '#fafafa',
   },
   section: {
      padding: '100px 0',
      backgroundColor: '#fafafa',
   },
   sectionDark: {
      padding: '100px 0',
      backgroundColor: '#0a0a0a',
   },
   container: {
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '0 5%',
   },
   yearHeader: {
      textAlign: 'center',
      marginBottom: '60px',
   },
   yearTitle: {
      fontFamily: "'Cormorant Garamond', serif",
      fontSize: 'clamp(2.5rem, 6vw, 4rem)',
      fontWeight: 300,
      color: '#0a0a0a',
      letterSpacing: '0.2em',
      margin: 0,
   },
   yearTitleLight: {
      fontFamily: "'Cormorant Garamond', serif",
      fontSize: 'clamp(2.5rem, 6vw, 4rem)',
      fontWeight: 300,
      color: 'white',
      letterSpacing: '0.2em',
      margin: 0,
   },
   divider: {
      width: '60px',
      height: '1px',
      backgroundColor: '#c9a227',
      margin: '20px auto 0',
   },
   eventsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '40px',
   },
   eventCard: {
      backgroundColor: 'white',
      overflow: 'hidden',
      transition: 'transform 0.4s ease, box-shadow 0.4s ease',
   },
   eventCardDark: {
      backgroundColor: '#1a1a1a',
      overflow: 'hidden',
      transition: 'transform 0.4s ease, box-shadow 0.4s ease',
   },
   imageWrapper: {
      position: 'relative',
      overflow: 'hidden',
      aspectRatio: '4/3',
   },
   eventImage: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      transition: 'transform 0.6s ease',
   },
   imagePlaceholder: {
      width: '100%',
      height: '100%',
      background: 'linear-gradient(135deg, #111, #2a2a2a)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      boxSizing: 'border-box',
   },
   placeholderTitle: {
      fontFamily: "'Cormorant Garamond', serif",
      fontSize: '1.6rem',
      fontWeight: 500,
      color: 'white',
      textAlign: 'center',
      lineHeight: 1.3,
   },
   eventContent: {
      padding: '30px',
   },
   eventDate: {
      fontFamily: "'Montserrat', sans-serif",
      fontSize: '0.8rem',
      letterSpacing: '0.1em',
      color: '#c9a227',
      marginBottom: '10px',
      textTransform: 'uppercase',
   },
   eventTitle: {
      fontFamily: "'Cormorant Garamond', serif",
      fontSize: '1.6rem',
      fontWeight: 500,
      color: '#0a0a0a',
      marginBottom: '10px',
   },
   eventTitleLight: {
      fontFamily: "'Cormorant Garamond', serif",
      fontSize: '1.6rem',
      fontWeight: 500,
      color: 'white',
      marginBottom: '10px',
   },
   eventVenue: {
      fontFamily: "'Montserrat', sans-serif",
      fontSize: '0.95rem',
      fontWeight: 300,
      color: '#666',
   },
   eventVenueLight: {
      fontFamily: "'Montserrat', sans-serif",
      fontSize: '0.95rem',
      fontWeight: 300,
      color: '#999',
   },
   eventTime: {
      fontFamily: "'Montserrat', sans-serif",
      fontSize: '0.85rem',
      fontWeight: 300,
      color: '#888',
      marginTop: '5px',
   },
   eventTimeLight: {
      fontFamily: "'Montserrat', sans-serif",
      fontSize: '0.85rem',
      fontWeight: 300,
      color: '#777',
      marginTop: '5px',
   },
   eventSubtitle: {
      fontFamily: "'Montserrat', sans-serif",
      fontSize: '0.85rem',
      fontWeight: 300,
      color: '#888',
      fontStyle: 'italic',
      marginTop: '8px',
   },
};

export default Events;
